/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

const ConnectionSummaryCard = ({
  label,
  title,
  subtitle,
  overrides,
  periodEndDates,
  showDates = true,
  fallbackText = "Not configured",
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "4px",
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.60)",
            lineHeight: "12px",
            letterSpacing: "0.3px",
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        {title && (
          <Typography variant="h6" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
        )}
        {subtitle && showDates && (
          <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.4px" }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {periodEndDates && showDates && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.4px" }}>
            Period End Dates
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
            {periodEndDates}
          </Typography>
        </Box>
      )}

      {overrides && overrides.length > 0 && showDates && (
        <Box>
          <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
            Property overrides:
          </Typography>
          {overrides.map((override, idx) => (
            <Typography key={idx} variant="body2" sx={{ color: "text.secondary" }}>
              {override.displayName || override.key_name}: {override.data_value || override.value}
            </Typography>
          ))}
        </Box>
      )}

      {!title && (
        <Typography
          variant="body2"
          sx={{
            color: "rgba(0, 0, 0, 0.38)",
            letterSpacing: "0.17px",
          }}
        >
          {fallbackText}
        </Typography>
      )}
    </Box>
  );
};

ConnectionSummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  overrides: PropTypes.arrayOf(
    PropTypes.shape({
      key_name: PropTypes.string,
      displayName: PropTypes.string,
      data_value: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  periodEndDates: PropTypes.string,
  showDates: PropTypes.bool,
  fallbackText: PropTypes.string,
};

export default ConnectionSummaryCard;
